import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { ConfirmModal } from '../ConfirmModal'
import { IconButton } from '../IconButton'
import { Modal } from '../Modal'
import { SortableTh } from '../SortableTh'
import { useTableSort } from '../../hooks/useTableSort'
import { supabase } from '../../lib/supabase'
import { formatNumber } from '../../lib/utils'
import type { AlimentoMacronutriente, Produto, StatusAlimentoMacro } from '../../types/database'
import {
  CATEGORIAS_ALIMENTO_SUGESTOES,
  STATUS_ALIMENTO_MACRO_LABELS,
} from '../../types/database'

type NutrientKey =
  | 'energia_kcal'
  | 'energia_kj'
  | 'carboidratos_g'
  | 'proteinas_g'
  | 'lipideos_g'
  | 'vitamina_a_mcg'
  | 'vitamina_c_g'
  | 'calcio_mg'
  | 'ferro_mg'

const NUTRIENT_FIELDS: { key: NutrientKey; label: string }[] = [
  { key: 'energia_kcal', label: 'Energia (kcal)' },
  { key: 'energia_kj', label: 'Energia (kJ)' },
  { key: 'carboidratos_g', label: 'Carboidratos (g)' },
  { key: 'proteinas_g', label: 'Proteínas (g)' },
  { key: 'lipideos_g', label: 'Lipídeos (g)' },
  { key: 'vitamina_a_mcg', label: 'Vitamina A (mcg)' },
  { key: 'vitamina_c_g', label: 'Vitamina C (g)' },
  { key: 'calcio_mg', label: 'Cálcio (mg)' },
  { key: 'ferro_mg', label: 'Ferro (mg)' },
]

type FormState = {
  nome: string
  categoria: string
  status: StatusAlimentoMacro
  produto_id: string
} & Record<NutrientKey, string>

function emptyForm(): FormState {
  return {
    nome: '',
    categoria: '',
    status: 'permitido',
    produto_id: '',
    energia_kcal: '0',
    energia_kj: '0',
    carboidratos_g: '0',
    proteinas_g: '0',
    lipideos_g: '0',
    vitamina_a_mcg: '0',
    vitamina_c_g: '0',
    calcio_mg: '0',
    ferro_mg: '0',
  }
}

function formFromRow(row: AlimentoMacronutriente): FormState {
  return {
    nome: row.nome,
    categoria: row.categoria || '',
    status: row.status,
    produto_id: row.produto_id || '',
    energia_kcal: String(row.energia_kcal ?? 0),
    energia_kj: String(row.energia_kj ?? 0),
    carboidratos_g: String(row.carboidratos_g ?? 0),
    proteinas_g: String(row.proteinas_g ?? 0),
    lipideos_g: String(row.lipideos_g ?? 0),
    vitamina_a_mcg: String(row.vitamina_a_mcg ?? 0),
    vitamina_c_g: String(row.vitamina_c_g ?? 0),
    calcio_mg: String(row.calcio_mg ?? 0),
    ferro_mg: String(row.ferro_mg ?? 0),
  }
}

function num(v: string) {
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

const PAGE_SIZE = 30

type Props = {
  onError: (msg: string | null) => void
  onInfo: (msg: string | null) => void
}

export function MacronutrientesSection({ onError, onInfo }: Props) {
  const { canWrite, canDelete } = useAuth()
  const writable = canWrite('macronutrientes')
  const [rows, setRows] = useState<AlimentoMacronutriente[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AlimentoMacronutriente | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const sort = useTableSort<AlimentoMacronutriente>('nome', 'asc')

  const load = useCallback(async () => {
    setLoading(true)
    onError(null)
    const [alimentosRes, produtosRes] = await Promise.all([
      supabase
        .from('alimentos_macronutrientes')
        .select('*, produto:produtos(nome, grupo, unidade)')
        .order('nome'),
      supabase.from('produtos').select('*').order('nome'),
    ])
    if (alimentosRes.error) onError(alimentosRes.error.message)
    else setRows((alimentosRes.data as AlimentoMacronutriente[]) || [])
    if (produtosRes.error) onError(produtosRes.error.message)
    else setProdutos((produtosRes.data as Produto[]) || [])
    setLoading(false)
  }, [onError])

  useEffect(() => {
    void load()
  }, [load])

  const filteredSorted = useMemo(() => {
    const q = busca.trim().toLowerCase()
    const filtered = q
      ? rows.filter((r) => {
          const statusLabel = STATUS_ALIMENTO_MACRO_LABELS[r.status] || r.status
          return (
            r.nome.toLowerCase().includes(q) ||
            (r.categoria || '').toLowerCase().includes(q) ||
            (r.produto?.nome || '').toLowerCase().includes(q) ||
            statusLabel.toLowerCase().includes(q)
          )
        })
      : rows
    const accessors: Record<string, (r: AlimentoMacronutriente) => string | number> = {
      nome: (r) => r.nome,
      produto: (r) => r.produto?.nome || '',
      categoria: (r) => r.categoria || '',
      status: (r) => STATUS_ALIMENTO_MACRO_LABELS[r.status] || r.status,
      energia_kcal: (r) => Number(r.energia_kcal) || 0,
      proteinas_g: (r) => Number(r.proteinas_g) || 0,
      carboidratos_g: (r) => Number(r.carboidratos_g) || 0,
      lipideos_g: (r) => Number(r.lipideos_g) || 0,
    }
    return sort.sortRows(filtered, accessors)
  }, [rows, busca, sort])

  const produtosDisponiveis = useMemo(() => {
    const vinculados = new Set(
      rows.filter((r) => r.produto_id && r.id !== editing?.id).map((r) => r.produto_id as string),
    )
    return produtos.filter((p) => !vinculados.has(p.id))
  }, [produtos, rows, editing?.id])

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE))

  useEffect(() => {
    setPage(1)
  }, [busca, sort.sortKey, sort.sortDir])

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages))
  }, [totalPages])

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filteredSorted.slice(start, start + PAGE_SIZE)
  }, [filteredSorted, page])

  const pageStart = filteredSorted.length ? (page - 1) * PAGE_SIZE + 1 : 0
  const pageEnd = Math.min(page * PAGE_SIZE, filteredSorted.length)

  function openNew() {
    setEditing(null)
    setForm(emptyForm())
    setModalOpen(true)
  }

  function openEdit(row: AlimentoMacronutriente) {
    setEditing(row)
    setForm(formFromRow(row))
    setModalOpen(true)
  }

  async function save() {
    if (!writable) return
    if (!form.nome.trim()) {
      onError('Nome é obrigatório')
      return
    }
    if (form.produto_id) {
      const duplicado = rows.find((r) => r.produto_id === form.produto_id && r.id !== editing?.id)
      if (duplicado) {
        onError('Este produto já está vinculado a outro alimento.')
        return
      }
    }
    setSaving(true)
    onError(null)
    const payload = {
      nome: form.nome.trim(),
      categoria: form.categoria.trim(),
      status: form.status,
      produto_id: form.produto_id || null,
      energia_kcal: num(form.energia_kcal),
      energia_kj: num(form.energia_kj),
      carboidratos_g: num(form.carboidratos_g),
      proteinas_g: num(form.proteinas_g),
      lipideos_g: num(form.lipideos_g),
      vitamina_a_mcg: num(form.vitamina_a_mcg),
      vitamina_c_g: num(form.vitamina_c_g),
      calcio_mg: num(form.calcio_mg),
      ferro_mg: num(form.ferro_mg),
      atualizado_em: new Date().toISOString(),
    }
    const q = editing
      ? supabase.from('alimentos_macronutrientes').update(payload).eq('id', editing.id)
      : supabase.from('alimentos_macronutrientes').insert(payload)
    const { error } = await q
    setSaving(false)
    if (error) {
      onError(
        error.code === '23505'
          ? 'Este produto já está vinculado a outro alimento.'
          : error.message,
      )
      return
    }
    setModalOpen(false)
    onInfo(editing ? 'Alimento atualizado.' : 'Alimento cadastrado.')
    await load()
  }

  async function confirmDelete() {
    if (!deleteId || !canDelete('macronutrientes')) return
    const { error } = await supabase.from('alimentos_macronutrientes').delete().eq('id', deleteId)
    setDeleteId(null)
    if (error) onError(error.message)
    else {
      onInfo('Alimento excluído.')
      await load()
    }
  }

  if (loading) return <div className="loading">Carregando macronutrientes…</div>

  return (
    <div>
      <div className="page-actions" style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {writable && (
          <button type="button" className="btn btn-primary" onClick={openNew}>
            Novo alimento
          </button>
        )}
        <div className="field" style={{ margin: 0, minWidth: 260, flex: '1 1 240px', maxWidth: 420 }}>
          <label htmlFor="macro-busca">Buscar alimento</label>
          <input
            id="macro-busca"
            type="search"
            placeholder="Nome, categoria ou status…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>
      <p className="kpi-sub" style={{ marginBottom: 12 }}>
        Valores nutricionais por <strong>100 g</strong> do alimento.
      </p>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh
                label="Nome"
                sortKey="nome"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="name"
              />
              <SortableTh
                label="Produto (preços)"
                sortKey="produto"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Categoria"
                sortKey="categoria"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Energia kcal"
                sortKey="energia_kcal"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="num"
              />
              <SortableTh
                label="Proteínas"
                sortKey="proteinas_g"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="num"
              />
              <SortableTh
                label="Carboidratos"
                sortKey="carboidratos_g"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="num"
              />
              <SortableTh
                label="Lipídeos"
                sortKey="lipideos_g"
                activeKey={sort.sortKey}
                direction={sort.sortDir}
                onSort={sort.toggleSort}
                col="num"
              />
              {(writable || canDelete('macronutrientes')) && <th className="col-actions">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id} className={r.status !== 'permitido' ? 'row-warn' : undefined}>
                <td className="col-name">{r.nome}</td>
                <td className="col-text">{r.produto?.nome || '—'}</td>
                <td className="col-text">{r.categoria || '—'}</td>
                <td className="col-text">{STATUS_ALIMENTO_MACRO_LABELS[r.status]}</td>
                <td className="col-num">{formatNumber(Number(r.energia_kcal), 2)}</td>
                <td className="col-num">{formatNumber(Number(r.proteinas_g), 2)}</td>
                <td className="col-num">{formatNumber(Number(r.carboidratos_g), 2)}</td>
                <td className="col-num">{formatNumber(Number(r.lipideos_g), 2)}</td>
                {(writable || canDelete('macronutrientes')) && (
                  <td className="col-actions">
                    {writable && (
                      <IconButton variant="edit" label={`Editar ${r.nome}`} onClick={() => openEdit(r)} />
                    )}
                    {canDelete('macronutrientes') && (
                      <IconButton
                        variant="delete"
                        label={`Excluir ${r.nome}`}
                        onClick={() => setDeleteId(r.id)}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!pageRows.length && (
              <tr>
                <td colSpan={9} className="kpi-sub">
                  {busca.trim() ? 'Nenhum alimento encontrado para a busca.' : 'Nenhum alimento cadastrado.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="escolas-table-footer">
        <div className="escolas-pagination-info kpi-sub">
          {filteredSorted.length
            ? `Exibindo ${pageStart}–${pageEnd} de ${filteredSorted.length} alimentos`
            : 'Nenhum alimento encontrado'}
        </div>
        <div className="escolas-pagination">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span className="escolas-pagination-label">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Próxima
          </button>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar alimento' : 'Novo alimento'}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Nome *</label>
            <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Produto na tabela de preços</label>
            <select
              value={form.produto_id}
              onChange={(e) => setForm({ ...form, produto_id: e.target.value })}
            >
              <option value="">Nenhum</option>
              {form.produto_id &&
                !produtosDisponiveis.some((p) => p.id === form.produto_id) && (
                  <option value={form.produto_id}>
                    {rows.find((r) => r.produto_id === form.produto_id)?.produto?.nome ||
                      produtos.find((p) => p.id === form.produto_id)?.nome ||
                      'Produto vinculado'}
                  </option>
                )}
              {produtosDisponiveis.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome} ({p.grupo}/{p.unidade})
                </option>
              ))}
            </select>
            <span className="kpi-sub">Cada produto pode ser vinculado a no máximo um alimento.</span>
          </div>
          <div className="field">
            <label>Categoria</label>
            <input
              list="categorias-alimento"
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
            />
            <datalist id="categorias-alimento">
              {CATEGORIAS_ALIMENTO_SUGESTOES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="field">
            <label>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as StatusAlimentoMacro })}
            >
              {(Object.keys(STATUS_ALIMENTO_MACRO_LABELS) as StatusAlimentoMacro[]).map((k) => (
                <option key={k} value={k}>
                  {STATUS_ALIMENTO_MACRO_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          {NUTRIENT_FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label>{f.label}</label>
              <input
                type="number"
                step="any"
                min={0}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteId}
        title="Excluir alimento"
        description="Remover este alimento do cadastro de macronutrientes?"
        confirmLabel="Excluir"
        variant="danger"
        onClose={() => setDeleteId(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}
